import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import moment from "moment";
import { Search, Plus, PhoneCall } from "lucide-react";
import { getPatients, clickToCall } from "../../api/api";
import DataTable from "../../components/DataTable";
import AddPatientModal from "../../components/AddPatientModal";
import PatientDrawer from "../../components/PatientDrawer";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, numberOfPage: 1 });
  const [addOpen, setAddOpen] = useState(false);
  const [drawerPatientId, setDrawerPatientId] = useState(null);
  const [callingId, setCallingId] = useState(null);
  const clickToCallEnabled = useSelector((s) => s.settings.data?.features?.click_to_call_enabled !== false);

  // Only starts the call (MCube rings the agent first, then bridges in the
  // patient) — the answered/missed toast comes later from the app-wide
  // "call_status_update" socket listener in App.js once MCube's webhook
  // reports the real outcome, same as PatientDrawer's click-to-call button.
  const handleQuickCall = async (patient, e) => {
    e.stopPropagation();
    setCallingId(patient._id);
    try {
      await clickToCall(patient._id);
      toast("Calling — you'll be rung first, then connected to the patient.");
    } catch (err) {
      toast.error(err?.message || "Could not place call.");
    } finally {
      setCallingId(null);
    }
  };

  const load = () => {
    setLoading(true);
    getPatients({ search: search || undefined, page, limit: 15 })
      .then((res) => {
        setPatients(res.data);
        setMeta({ total: res.total, numberOfPage: res.numberOfPage });
      })
      .catch((err) => toast.error(err?.message || "Could not load patients."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  const columns = [
    {
      key: "name",
      header: "Patient",
      render: (p) => (
        <div>
          <p className="font-medium">{p.name}</p>
          <p className="text-xs text-muted-foreground">{p.phone}</p>
        </div>
      ),
    },
    {
      key: "location",
      header: "Location",
      render: (p) => [p.address?.city, p.address?.state].filter(Boolean).join(", ") || "—",
    },
    { key: "gender", header: "Gender", render: (p) => p.gender || "—" },
    { key: "appointment_count", header: "Visits" },
    {
      key: "last_visit",
      header: "Last visit",
      render: (p) => (p.last_visit ? moment(p.last_visit).format("D MMM YYYY") : "—"),
    },
    ...(clickToCallEnabled
      ? [
          {
            key: "call",
            header: "",
            render: (p) => (
              <Button size="sm" variant="outline" disabled={callingId === p._id} onClick={(e) => handleQuickCall(p, e)}>
                <PhoneCall className="h-3.5 w-3.5" /> {callingId === p._id ? "Calling…" : "Call"}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Patients</h2>
          <p className="text-sm text-muted-foreground">Your clinic's registered patient records</p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" /> Add patient
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by name or phone…"
          className="pl-9"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      <DataTable
        columns={columns}
        data={patients}
        loading={loading}
        emptyMessage="No patients found."
        page={page}
        numberOfPage={meta.numberOfPage}
        onPageChange={setPage}
        onRowClick={(p) => setDrawerPatientId(p._id)}
        rowKey={(p) => p._id}
      />

      <AddPatientModal open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <PatientDrawer patientId={drawerPatientId} onClose={() => setDrawerPatientId(null)} />
    </div>
  );
}
